import React, {useContext, useState} from "react";
import {useSearchParams} from "react-router-dom";
import {Box, IconButton, InputBase, Stack, Typography, useMediaQuery, useTheme} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {CustomerSupportIcon, SendIcon} from "@hugeicons/core-free-icons";
import {useRegistryProject} from "../RegistryProjectContext";
import {useSubProjectFlows} from "../useSubProjectFlows";
import {useStompWebsocket} from "../../../hooks/useStompWebsocket";
import {SignInContext} from "../../../context/SignInContext";
import {ConversationRepository} from "../../../repositories/ConversationRepository";
import {MobileUnavailable} from "../../../components/MobileUnavailable";
import {PANEL_SX} from "../dashboard/components/Panel";
import {SupervisionList} from "./SupervisionList";
import {SupervisionDetail} from "./SupervisionDetail";

// Project type -> conversation type
const CONVERSATION_TYPES = {VOICE_ASSISTANT: "VOICE_BOT", CHATBOT: "CHAT_BOT"};

// Nothing selected. Chatbot projects can start a test conversation on the selected language's flow.
const EmptyState = ({isChat, flow, onStarted}) => {
  const {currentUser} = useContext(SignInContext);
  const {members} = useRegistryProject();
  const [message, setMessage] = useState("");

  const start = (e) => {
    e.preventDefault();
    if (!message.trim() || !flow?.id) return;
    // the user's VDN from the project members (the flow's vdnManager is no longer updated)
    const vdn = members.find(member => member.username === currentUser?.username)?.vdn;
    const sender = localStorage.getItem("sender") || vdn || -999;
    ConversationRepository.createConversation({flowUid: flow.id, sender, message, type: "CHAT_BOT"})
      .then(response => {
        setMessage("");
        if (response?.data?.id) onStarted(response.data);
      })
      .catch(error => console.log(error));
  };

  return (
    <Stack alignItems={"center"} justifyContent={"center"} gap={1.5} sx={{height: "100%", px: 3, textAlign: "center"}}>
      <Box sx={{
        width: 56, height: 56, borderRadius: "16px", display: "flex", alignItems: "center", justifyContent: "center",
        bgcolor: "rgba(132, 145, 247, 0.12)", color: "#8491F7",
      }}>
        <HugeiconsIcon icon={CustomerSupportIcon} size={26}/>
      </Box>
      <Typography sx={{fontSize: 18, fontWeight: 700, color: "#23264D"}}>Select a conversation</Typography>
      <Typography sx={{fontSize: 13, color: "#8A8AA0", maxWidth: 360}}>
        Pick a conversation from the list to follow it live, supervise it or hand it over.
      </Typography>
      {isChat && flow?.id && (
        <Box component={"form"} onSubmit={start} sx={{
          mt: 2, width: "100%", maxWidth: 420, display: "flex", alignItems: "center", gap: 1, pl: 1.75, pr: 0.5,
          height: 44, borderRadius: "12px", border: "1px solid #E4E4EE", bgcolor: "#fff",
          "&:focus-within": {borderColor: "#8491F7"},
        }}>
          <InputBase
            placeholder={"Start a test conversation…"}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            sx={{flex: 1, fontSize: 14}}
          />
          <IconButton type={"submit"} disabled={!message.trim()} sx={{color: "#4B55C9"}}>
            <HugeiconsIcon icon={SendIcon} size={18}/>
          </IconButton>
        </Box>
      )}
    </Stack>
  );
};

// Live supervision of the project's conversations (every language, or one picked in the list).
// The open conversation is kept in the URL (?conversation=123).
export const RegistrySupervisionPage = () => {
  const {project, subProject} = useRegistryProject();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [searchParams, setSearchParams] = useSearchParams();
  const conversationId = searchParams.get("conversation");

  // "" = every language
  const [subProjectId, setSubProjectId] = useState("");
  const lookups = useSubProjectFlows(project);
  const conversationType = CONVERSATION_TYPES[project.type] || "VOICE_BOT";

  // Conversations created or changed, and the live number of active ones, for the picked language or the project
  const scope = subProjectId ? `/topic/sub-project/${subProjectId}` : `/topic/project/${project.id}`;
  const [conversationUpdate] = useStompWebsocket(`${scope}/conversations`);
  const [activeUpdate] = useStompWebsocket(`${scope}/active-conversations`);
  const activeCount = activeUpdate && (!activeUpdate.conversationType || activeUpdate.conversationType === conversationType)
    ? Number(activeUpdate.active) || 0
    : undefined;

  const select = (conversation) => setSearchParams(conversation ? {conversation: String(conversation.id)} : {});

  if (isMobile) {
    return <MobileUnavailable/>;
  }

  return (
    <Box sx={{flex: 1, minHeight: 0, display: "flex", gap: 2, p: 2}}>
      <Box sx={{...PANEL_SX, width: {md: 340, xl: 380}, flexShrink: 0, overflow: "hidden", display: "flex", flexDirection: "column"}}>
        <SupervisionList
          projectId={project.id}
          subProjects={project.subProjects}
          conversationType={conversationType}
          subProjectId={subProjectId}
          onChangeLanguage={setSubProjectId}
          lookups={lookups}
          selectedId={conversationId}
          onSelect={select}
          conversationUpdate={conversationUpdate}
          activeUpdate={activeUpdate}
          activeCount={activeCount}
        />
      </Box>
      <Box sx={{...PANEL_SX, flex: 1, minWidth: 0, overflow: "hidden", display: "flex", flexDirection: "column"}}>
        {conversationId ? (
          <SupervisionDetail
            key={conversationId}
            conversationId={conversationId}
            flowOf={lookups.flowOf}
            fallbackFlow={lookups.flows[subProject.flowUid] || null}
            subProjectOf={lookups.subProjectOf}
            tagsOf={lookups.tagsOf}
            conversationUpdate={conversationUpdate}
            onOpen={select}
            onClose={() => select(null)}
          />
        ) : (
          <EmptyState
            isChat={conversationType === "CHAT_BOT"}
            flow={lookups.flows[subProject.flowUid]}
            onStarted={select}
          />
        )}
      </Box>
    </Box>
  );
};
